import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, faqs } from '@/mocks/comics';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <main className="pt-16 md:pt-20">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        {/* Hero */}
        <section className="py-10 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-3">
              关于蜜桃少年漫画
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              蜜桃少年漫画致力于为广大漫画爱好者提供漫画全集免费阅读下拉式体验，与土豪漫画网深度合作，让每一位读者都能享受免费且流畅的阅读乐趣。
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="py-10 md:py-14" aria-labelledby="brand-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 id="brand-title" className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">
              品牌介绍
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                  <i className="ri-book-open-line text-xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">海量漫画资源</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  蜜桃少年漫画汇聚了少年、恋爱、热血、搞笑、玄幻、悬疑、科幻、动作等多种类型的优质漫画作品。每一部漫画都经过精心筛选，确保为读者提供最优质的阅读内容。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                  <i className="ri-smartphone-line text-xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">下拉式流畅阅读</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  采用漫画全集免费阅读下拉式技术，读者只需向下滑动即可连续阅读，无需频繁翻页。沉浸式阅读体验让您完全融入漫画世界，享受最流畅的阅读感受。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
                  <i className="ri-shield-check-line text-xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">完全免费</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们承诺所有漫画内容完全免费阅读，无需任何付费订阅。与土豪漫画网合作，持续为读者提供高质量的免费漫画资源，让漫画爱好者能够无忧畅读。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 md:py-14 bg-background-100" aria-label="平台数据">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {[
                { num: '2000+', label: '漫画作品' },
                { num: '50万+', label: '日活跃读者' },
                { num: '99.9%', label: '服务可用率' },
                { num: '4.9', label: '用户评分' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-4 rounded-lg">
                  <div className="text-2xl md:text-3xl font-heading text-primary-500 mb-1">{stat.num}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-10 md:py-14" aria-labelledby="testimonial-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 id="testimonial-title" className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 md:p-6 rounded-lg bg-background-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: t.rating }, (_, i) => (
                          <i key={i} className="ri-star-fill text-xs text-yellow-500"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-14 bg-background-100" aria-labelledby="faq-title">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 id="faq-title" className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="rounded-lg bg-background-50 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    aria-expanded={openFaq === index}
                  >
                    <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</span>
                    <i
                      className={`text-foreground-400 transition-transform flex-shrink-0 ${
                        openFaq === index ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-14" aria-labelledby="contact-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 id="contact-title" className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <div className="p-6 rounded-lg bg-background-100 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-500">contact@mitaoshaonian.com</p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-3">
                  <i className="ri-wechat-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
                <p className="text-sm text-foreground-500">蜜桃少年漫画</p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-3">
                  <i className="ri-qq-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">QQ粉丝群</h3>
                <p className="text-sm text-foreground-500">群号：888777666</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* AboutPage & FAQPage Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于蜜桃少年漫画',
          description: '蜜桃少年漫画致力于为广大漫画爱好者提供漫画全集免费阅读下拉式体验，与土豪漫画网深度合作。',
          url: 'https://mitaoshaonian.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '蜜桃少年漫画',
            url: 'https://mitaoshaonian.com/',
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mitaoshaonian.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.a,
            },
          })),
        })}
      </script>
    </>
  );
}